import { setActiveIndex } from "@/core/redux/slices/specification-template";
import { useDispatch, useSelector } from "@/core/redux/store";
import { getSpecificationTemplate } from "@/core/redux/thunks/specificationTemplateThunk";
import { ROUTES } from "@/core/router/routes";
import { Button } from "@/shared/components/ui/button";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import { ISpecificationTemplate } from "@/shared/services/interface/responses/ISpecificationTemplate";
import { getDashboardLink } from "@/shared/utils/link";
import { getEnv } from "@/shared/utils/env";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  CircleHelp,
  Copy,
  Megaphone,
  X,
} from "lucide-react";

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "./specification-template.scss";

export const DialogDetail = ({
  open,
  handleOpenChange,
  template,
  handleCloneSpecification,
}: {
  open: boolean;
  handleOpenChange: (open: boolean) => void;
  template: ISpecificationTemplate;
  handleCloneSpecification: () => void | Promise<void>;
}) => {
  const { t } = useTranslation();
  const { detail, activeIndex, list } = useSelector(
    (state) => state.specificationTemplate
  );
  const [arrowActive, setArrowActive] = useState(true);
  const [currentTemplate, setCurrentTemplate] =
    useState<ISpecificationTemplate | null>(template);
  const [isCloning, setIsCloning] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleCloneClick = useCallback(async () => {
    if (isCloning) return;
    setIsCloning(true);
    try {
      await Promise.resolve(handleCloneSpecification());
    } finally {
      setIsCloning(false);
    }
  }, [handleCloneSpecification, isCloning]);

  const handleOpenLive = () => {
    navigate(
      getDashboardLink(ROUTES.Dashboard.Specification.Create) +
        `?specificationId=${currentTemplate?.specificationId}&isIframe=true&iframe=true`
    );
  };

  const handlePrevious = () => {
    if (activeIndex === 0) return;
    setArrowActive(false);
    const newIndex = activeIndex - 1;
    const newTemplate = list[newIndex];
    setCurrentTemplate(newTemplate);
    dispatch(getSpecificationTemplate(newTemplate.id));
    dispatch(setActiveIndex(newIndex));
  };

  const handleNext = () => {
    if (activeIndex === list.length - 1) return;
    setArrowActive(false);
    const newIndex = activeIndex + 1;
    const newTemplate = list[newIndex];
    setCurrentTemplate(newTemplate);
    dispatch(getSpecificationTemplate(newTemplate.id));
    dispatch(setActiveIndex(newIndex));
  };

  useEffect(() => {
    setTimeout(() => {
      setArrowActive(true);
    }, 500);
  }, [detail]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        closeButton={false}
        className="max-w-max scrollbar-hide bg-transparent border-none"
      >
        <div className="left absolute top-[276px] -left-[20px]">
          <Button
            variant="default"
            className="flex items-center w-[30px] h-[30px] rounded-[6px] bg-transparent hover:bg-black"
            aria-label="template-previous"
            disabled={activeIndex === 0 || !arrowActive}
            onClick={handlePrevious}
          >
            <ArrowLeft
              style={{ width: "22px", height: "22px" }}
              className="text-white"
            />
          </Button>
        </div>
        <div className="right absolute top-[276px] -right-[20px]">
          <Button
            variant="default"
            className="flex items-center rounded-[6px] w-[30px] h-[30px]  bg-transparent hover:bg-black"
            aria-label="template-next"
            disabled={activeIndex === list.length - 1 || !arrowActive}
            onClick={handleNext}
          >
            <ArrowRight
              style={{ width: "22px", height: "22px" }}
              className="text-white"
            />
          </Button>
        </div>
        <div className="right max-w-max absolute top-[35px] -right-[20px]">
          <Button
            variant="default"
            className="flex items-center rounded-[6px] w-[30px] h-[30px]  bg-transparent hover:bg-black"
            aria-label="dialog-close"
            onClick={() => handleOpenChange(false)}
          >
            <X className="w-[22px] h-[22px] text-white" />
          </Button>
        </div>
        <div className="right max-w-max absolute top-[65px] -right-[20px]">
          <Button
            variant="default"
            className="flex items-center rounded-[6px] w-[30px] h-[30px]  bg-transparent hover:bg-black"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="23"
              viewBox="0 0 24 23"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13.1904 4.28516H18.1904H18.9047V4.99944V9.99944H17.4762V6.72388L7.05775 17.1423H10.3333V18.5709H5.3333H4.61902V17.8566V12.8566H6.04759V16.1322L16.466 5.71373H13.1904V4.28516Z"
                fill="white"
              />
            </svg>
          </Button>
        </div>
        <div className="w-[1220px] px-[50px] py-[56px] h-[750px] border bg-background overflow-y-auto rounded-[12px] shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)] relative bg-white">
          <div className="w-full flex justify-between">
            <div>
              <span className="text-[22px] font-600 text-black">
                {currentTemplate?.specification.name}
              </span>
              <p className="text-[13.3px] font-600 text-[#3F3F46B2]">
                {currentTemplate?.specification.name}
              </p>
            </div>
            <div className="flex items-center gap-2.5">
              <Button
                variant="outline"
                className="flex items-center gap-1 rounded-[6px] h-[42px]"
                onClick={handleOpenLive}
              >
                <span className="text-[14px] font-600 text-[#18181B]">
                  {t("open-live")}
                </span>
                <ArrowUpRight
                  style={{ width: "16px", height: "16px" }}
                  className="text-[#18181B] -ml-[2px]"
                />
              </Button>
              <Button
                variant="default"
                onClick={handleCloneClick}
                isLoading={isCloning}
                className="flex items-center gap-1 rounded-[6px] h-[42px]"
              >
                <Copy
                  style={{ width: "16px", height: "16px" }}
                  className="text-[#FAFAFA]"
                />
                <span className="text-[14px] font-600 text-[#FAFAFA]">
                  {t("clone-in-procright")}
                </span>
              </Button>
            </div>
          </div>
          <div className="w-full iframe-container relative flex rounded-[6px] h-[602px] overflow-hidden mt-[31px] shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)]">
            <iframe
              src={`${getEnv("VITE_DASHBOARD_URL")}/dashboard/specification/create?specificationId=${
                currentTemplate?.specificationId
              }&isIframe=true&iframe=true`}
              className="w-full h-full"
            />
            <Button
              variant="secondary"
              className="absolute bottom-[25px]  left-[54%]   max-w-max h-[35px]  z-[20] hover:bg-zinc-200 hover:border-zinc-300 hover:border"
              onClick={handleCloneClick}
              isLoading={isCloning}
              loadingColor="black"
            >
              <span className="mr-[127px]">{t("clone-in-procright")}</span>
              <svg
                className="-mt-[1px]"
                xmlns="http://www.w3.org/2000/svg"
                width="19"
                height="14"
                viewBox="0 0 19 14"
                fill="none"
              >
                <path
                  d="M7.52392 3.53947H10.9066V6.0437H13.4057C13.4057 4.37532 13.4057 2.66607 13.4057 1.00015H5.00534V6.95508H7.52908L7.52392 3.53947Z"
                  fill="black"
                />
                <path d="M7.52116 9.47884H5V12H7.52116V9.47884Z" fill="black" />
                <path
                  d="M10.0501 6.95736H7.52896V9.47852H10.0501V6.95736Z"
                  fill="black"
                />
                <path
                  d="M10.0501 9.4834L13.4092 6.04334H10.0501V9.4834Z"
                  fill="#3F3F46"
                />
              </svg>
            </Button>
            {/* <div className="absolute top-0 left-0 w-full h-full  z-[10]"> </div> */}
          </div>
          <div className="w-full flex justify-between items-start mt-[31px] specification-template-detail-box">
            <div className="w-[690px]">
              <span className="text-[18px] font-600 text-zinc-900">
                {t("description")}
              </span>
              <div className="mt-[16px] text-[16px] font-500 text-muted-foreground">
                <span>{detail?.description}</span>
              </div>
              <p className="font-700 text-zinc-950 mt-[30px]">
                {t("for-whom")}:
              </p>
              <ul className="list-disc list-inside ml-3 font-500 text-muted-foreground ">
                {detail?.forWhom?.map((item: string, index: number) => (
                  <li key={index}>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="font-700 text-zinc-950 mt-[30px]">
                {t("template-purpose")}:
              </p>
              <ul className="list-disc list-inside ml-3 font-500 text-muted-foreground mt-[10px]">
                {detail?.templatePurposes?.map(
                  (
                    item: { title: string; description: string },
                    index: number
                  ) => (
                    <li key={index}>
                      <span className="font-700 text-muted-foreground mr-[2px]">
                        {item.title}:{" "}
                      </span>
                      {item.description}
                    </li>
                  )
                )}
              </ul>
            </div>
            <div className="w-[270px] ">
              <div>
                <span className="text-[16px] font-600 text-zinc-900">
                  {t("tags")}
                </span>
                <div className="mt-[10px] flex gap-1 flex-wrap">
                  {detail?.tags?.map((tag: string) => (
                    <div
                      key={tag}
                      className="max-w-max h-[26px] text-[#18181B] text-[12px] font-500 bg-zinc-100 rounded-[6px] px-[10px] flex items-center justify-center"
                    >
                      {tag}
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-[16px]">
                <span className="text-[16px] font-600 text-zinc-900">
                  {t("support")}
                </span>
                <div className="mt-[10px] space-y-[10px]">
                  <div className="flex items-center gap-2">
                    <CircleHelp
                      style={{ width: "16px", height: "16px" }}
                      className="text-foreground"
                    />
                    <span className="text-[13px] font-500 text-zinc-700 mt-[1.5px]">
                      {t("get-help")}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Megaphone
                      style={{ width: "16px", height: "16px" }}
                      className="text-foreground"
                    />
                    <span className="text-[13px] font-500 text-zinc-700 mt-[1.5px]">
                      {t("report-this-template")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
